/*
  Pick A Toy custom: sticky add-to-cart bar (sections/pat-sticky-atc.liquid).
  The bar never submits anything itself. Its button clicks the main product form's
  submit button, so Dawn's own add-to-cart flow (variant, quantity, errors, cart
  notification) runs unchanged.
*/
if (!customElements.get('pat-sticky-atc')) {
  customElements.define(
    'pat-sticky-atc',
    class PatStickyAtc extends HTMLElement {
      connectedCallback() {
        this.productInfo = document.querySelector('product-info[id^="MainProduct-"]');
        if (!this.productInfo) return;

        this.sectionId = this.productInfo.dataset.section;
        this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (!this.mainButton) return;

        this.bar = this.querySelector('.pat-sticky__bar');
        this.button = this.querySelector('.pat-sticky__button');
        this.label = this.querySelector('.pat-sticky__label');
        this.price = this.querySelector('.pat-sticky__price');
        this.thumb = this.querySelector('.pat-sticky__thumb');

        this.mainAbove = false;
        this.footerVisible = false;

        this.removeAttribute('hidden');
        this.setVisible(false);

        // Shopify's preview bar (theme preview links and the theme editor only, never on the live
        // store) is fixed to the bottom of the window and would cover the bar, so clicks would land
        // on it. Lift the bar above it. It can be injected after this element connects, so look again
        // once the page has loaded.
        this.liftAbovePreviewBar = this.liftAbovePreviewBar.bind(this);
        if (!this.watchPreviewBar()) window.addEventListener('load', () => this.watchPreviewBar(), { once: true });

        this.onClick = this.onClick.bind(this);
        this.button.addEventListener('click', this.onClick);

        // The main button's root reaches far below the viewport, so the button only "leaves" by
        // passing the top edge. A jump that skips over it in one frame (an anchor link from below
        // the fold) still flips the state; with a plain viewport root it would never fire.
        this.mainObserver = new IntersectionObserver(this.onMainIntersect.bind(this), {
          rootMargin: '0px 0px 100000px 0px',
        });
        this.mainObserver.observe(this.mainButton);

        this.footer = document.querySelector('.footer-group, footer, .footer');
        if (this.footer) {
          this.footerObserver = new IntersectionObserver(this.onFooterIntersect.bind(this));
          this.footerObserver.observe(this.footer);
        }

        this.mirrorButton();
        this.mutationObserver = new MutationObserver(() => this.mirrorButton());
        this.mutationObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
          this.unsubscribeError = subscribe(PUB_SUB_EVENTS.cartError, (event) => this.onCartError(event));
        }
      }

      disconnectedCallback() {
        if (this.mainObserver) this.mainObserver.disconnect();
        if (this.footerObserver) this.footerObserver.disconnect();
        if (this.mutationObserver) this.mutationObserver.disconnect();
        if (this.previewObserver) this.previewObserver.disconnect();
        window.removeEventListener('resize', this.liftAbovePreviewBar);
        if (this.unsubscribeVariant) this.unsubscribeVariant();
        if (this.unsubscribeError) this.unsubscribeError();
        if (this.button) this.button.removeEventListener('click', this.onClick);
      }

      onMainIntersect(entries) {
        const entry = entries[entries.length - 1];
        this.mainAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.setVisible(this.mainAbove && !this.footerVisible);
      }

      onFooterIntersect(entries) {
        this.footerVisible = entries[entries.length - 1].isIntersecting;
        this.setVisible(this.mainAbove && !this.footerVisible);
      }

      watchPreviewBar() {
        if (this.previewBar) return true;
        this.previewBar = document.getElementById('PBarNextFrameWrapper');
        if (!this.previewBar) return false;

        if ('ResizeObserver' in window) {
          this.previewObserver = new ResizeObserver(this.liftAbovePreviewBar);
          this.previewObserver.observe(this.previewBar);
        }
        window.addEventListener('resize', this.liftAbovePreviewBar);
        this.liftAbovePreviewBar();
        return true;
      }

      liftAbovePreviewBar() {
        if (!this.previewBar || !this.previewBar.isConnected) {
          this.style.removeProperty('--pat-sticky-lift');
          return;
        }
        const rect = this.previewBar.getBoundingClientRect();
        const lift = rect.height > 0 ? Math.max(0, Math.round(window.innerHeight - rect.top)) : 0;
        this.style.setProperty('--pat-sticky-lift', `${lift}px`);
      }

      setVisible(visible) {
        this.classList.toggle('is-visible', visible);
        this.bar.toggleAttribute('inert', !visible);
        this.bar.setAttribute('aria-hidden', String(!visible));
      }

      mirrorButton() {
        const disabled =
          this.mainButton.hasAttribute('disabled') || this.mainButton.getAttribute('aria-disabled') === 'true';
        this.button.disabled = disabled;
        this.button.classList.toggle('loading', this.mainButton.classList.contains('loading'));

        const mainLabel = this.mainButton.querySelector(':scope > span');
        const text = mainLabel ? mainLabel.textContent.trim() : '';
        if (text && this.label.textContent !== text) this.label.textContent = text;
      }

      onClick() {
        if (this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true') return;
        this.mainButton.click();
      }

      onVariantChange(event) {
        if (!event || !event.data || event.data.sectionId !== this.sectionId) return;

        const source = document.querySelector(`#price-${this.sectionId} .price`);
        if (source && this.price) this.price.innerHTML = source.outerHTML;

        const media = event.data.variant && event.data.variant.featured_media;
        const src = media && media.preview_image && media.preview_image.src;
        if (src && this.thumb) {
          const url = new URL(src, window.location.origin);
          url.searchParams.set('width', '120');
          url.searchParams.set('height', '120');
          url.searchParams.set('crop', 'center');
          this.thumb.removeAttribute('srcset');
          this.thumb.src = url.toString();
          if (media.alt) this.thumb.alt = media.alt;
        }
      }

      onCartError(event) {
        if (!this.classList.contains('is-visible')) return;
        if (event && event.source && event.source !== 'product-form') return;
        const error = this.productInfo.querySelector('.product-form__error-message-wrapper');
        if (error) error.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  );
}
